/**
 * Stations this browser has opened, most recent first.
 *
 * Stations are Durable Objects addressed by name with no registry, so there is
 * nothing on the server to enumerate. A server-side index would have to be
 * written on creation and, with no user model, would list every station anyone
 * ever made to anyone who reaches the apex. This keeps the list local instead:
 * it answers "the stations I already have" honestly and discloses nothing.
 */

const STORAGE_KEY = "workerstation.recent-stations";
/** Bounded so a long-lived browser cannot grow the entry without limit. */
export const RECENT_STATION_LIMIT = 20;

export interface RecentStation {
  readonly stationId: string;
  /** Epoch millis of the most recent visit. */
  readonly openedAt: number;
}

type StorageLike = Pick<Storage, "getItem" | "setItem">;

function storage(): StorageLike | undefined {
  try {
    // Access can throw outright when storage is disabled, not just return null.
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

function isRecentStation(value: unknown): value is RecentStation {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.stationId === "string" &&
    candidate.stationId.length > 0 &&
    typeof candidate.openedAt === "number" &&
    Number.isFinite(candidate.openedAt)
  );
}

/**
 * Read the list, newest first. Unusable or corrupt storage yields an empty
 * list: a launcher with no history is a fine outcome, a thrown error is not.
 */
export function readRecentStations(): RecentStation[] {
  const store = storage();
  if (!store) return [];
  let raw: string | null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return [];
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    return parsed
      .filter(isRecentStation)
      .filter((entry) => {
        if (seen.has(entry.stationId)) return false;
        seen.add(entry.stationId);
        return true;
      })
      .sort((left, right) => right.openedAt - left.openedAt)
      .slice(0, RECENT_STATION_LIMIT);
  } catch {
    return [];
  }
}

function write(entries: readonly RecentStation[]): RecentStation[] {
  const next = entries.slice(0, RECENT_STATION_LIMIT);
  const store = storage();
  if (store) {
    try {
      store.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Quota or a private-mode rejection: the in-memory result is still
      // correct for this render, so report it rather than failing the caller.
    }
  }
  return next;
}

/** Record a visit, moving the station to the front. Returns the new list. */
export function recordStationVisit(
  stationId: string,
  openedAt: number = Date.now()
): RecentStation[] {
  if (!stationId) return readRecentStations();
  const rest = readRecentStations().filter(
    (entry) => entry.stationId !== stationId
  );
  return write([{ stationId, openedAt }, ...rest]);
}

/** Drop a station from the list. Returns the new list. */
export function forgetStation(stationId: string): RecentStation[] {
  return write(
    readRecentStations().filter((entry) => entry.stationId !== stationId)
  );
}
